import { useEffect, useState } from "react";
import { Share2, Loader2, Users, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/** Print / save-as-PDF via a hidden iframe (no pop-ups, keeps colors, tables and images). */
export function printNote(note) {
  const css = [...document.querySelectorAll('link[rel="stylesheet"], style')].map((el) => el.outerHTML).join("\n");
  const meta = [note.author_name, note.subject_name, `Updated ${new Date(note.updated_at).toLocaleString()}`].filter(Boolean).join(" · ");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${(note.title || "Note").replace(/</g, "&lt;")}</title>${css}
<style>@page{margin:18mm} body{background:#fff;margin:0;padding:0;font-family:Poppins,'Segoe UI',system-ui,-apple-system,sans-serif;color:#0f172a} @font-face{font-family:Poppins;src:url(/fonts/poppins/Poppins-Regular.ttf)} @font-face{font-family:Poppins;src:url(/fonts/poppins/Poppins-Bold.ttf);font-weight:bold}
h1.note-title{font-size:26px;margin:0 0 4px} .note-meta{color:#64748b;font-size:12px;margin-bottom:18px;border-bottom:1px solid #e2e8f0;padding-bottom:10px}
.ql-editor{padding:0;white-space:normal;overflow:visible;height:auto} .ql-editor img{max-width:100%} .ql-editor table{border-collapse:collapse} .ql-editor td,.ql-editor th{border:1px solid #94a3b8;padding:4px 8px}</style></head>
<body><h1 class="note-title">${(note.title || "Note").replace(/</g, "&lt;")}</h1><div class="note-meta">${meta.replace(/</g, "&lt;")}</div>
<div class="ql-snow"><div class="ql-editor">${note.html || ""}</div></div></body></html>`;
  const frame = document.createElement("iframe");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
  frame.setAttribute("data-testid", "note-print-frame");
  document.body.appendChild(frame);
  frame.srcdoc = html;
  frame.onload = () => {
    const w = frame.contentWindow;
    const done = () => setTimeout(() => frame.remove(), 1500);
    w.onafterprint = done;
    setTimeout(() => { w.focus(); w.print(); }, 400);
  };
}

/** Download the note as a real PDF file (rendered server-side). */
export async function downloadNotePdf(note) {
  try {
    const res = await api.get(`/notes/${note.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(note.title || "note").replace(/[^\w-]+/g, "-").slice(0, 60) || "note"}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch { toast.error("Could not download PDF"); }
}

export function ShareDialog({ note, onClose, onShared }) {
  const [opts, setOpts] = useState(null);
  const [users, setUsers] = useState(new Set(note.shared_with || []));
  const [classes, setClasses] = useState(new Set(note.shared_class_ids || []));
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/notes/share-options").then(({ data }) => setOpts(data)).catch(() => setOpts({ users: [], classes: [] })); }, []);

  const toggle = (set, setter, id) => { const n = new Set(set); n.has(id) ? n.delete(id) : n.add(id); setter(n); };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/notes/${note.id}/share`, { user_ids: [...users], class_ids: [...classes] });
      toast.success(users.size + classes.size ? "Sharing updated" : "Note is now private"); onShared(data); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="note-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Share2 className="h-5 w-5 text-primary" /> Share “{note.title}”</DialogTitle>
          <DialogDescription>Colleagues you pick can read this note. Sharing with a class lets every teacher of that class read it.</DialogDescription>
        </DialogHeader>
        {!opts ? <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[50vh] overflow-y-auto">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5 inline-flex items-center gap-1"><Users className="h-3 w-3" /> Colleagues</div>
              {opts.users.length === 0 && <div className="text-xs text-slate-400">No other staff yet.</div>}
              {opts.users.map((u) => (
                <label key={u.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer" data-testid={`share-user-${u.id}`}>
                  <input type="checkbox" checked={users.has(u.id)} onChange={() => toggle(users, setUsers, u.id)} className="h-4 w-4 accent-[hsl(var(--primary))]" />
                  <span className="truncate">{u.name}</span><span className="text-[10px] uppercase text-slate-400 ml-auto">{u.role}</span>
                </label>
              ))}
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5 inline-flex items-center gap-1"><GraduationCap className="h-3 w-3" /> Whole class</div>
              {opts.classes.length === 0 && <div className="text-xs text-slate-400">No classes.</div>}
              {opts.classes.map((c) => (
                <label key={c.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer" data-testid={`share-class-${c.id}`}>
                  <input type="checkbox" checked={classes.has(c.id)} onChange={() => toggle(classes, setClasses, c.id)} className="h-4 w-4 accent-[hsl(var(--primary))]" />
                  <span className="truncate">{c.name}</span>{c.grade && <span className="text-[10px] text-slate-400 ml-auto">Gr {c.grade}</span>}
                </label>
              ))}
            </div>
          </div>
        )}
        <button onClick={save} disabled={busy || !opts} data-testid="note-share-save" className="w-full h-11 rounded-lg bg-primary text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Save sharing ({users.size + classes.size})
        </button>
      </DialogContent>
    </Dialog>
  );
}
